import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import {
  getAllOrders,
  updateOrderStatus,
  getOrderStats,
  ORDER_STATUS,
  getOrderStatusConfig,
  formatDate,
  getTimeAgo,
} from "../services/api";

// Labels personnalisés pour l'interface admin (plus professionnels)
const ADMIN_ORDER_STATUS_LABELS = {
  [ORDER_STATUS.EN_ATTENTE_PAIEMENT]: "En attente de paiement",
  [ORDER_STATUS.PAYEE]: "Commande reçue",
  [ORDER_STATUS.EN_PREPARATION]: "En cours de préparation",
  [ORDER_STATUS.EXPEDIEE]: "Commande expédiée",
  [ORDER_STATUS.LIVREE]: "Commande livrée",
  [ORDER_STATUS.ANNULEE]: "Commande annulée",
  [ORDER_STATUS.RETOURNEE]: "Commande retournée",
};

// Configuration personnalisée des statuts pour l'admin
const getAdminOrderStatusConfig = (status) => {
  const baseConfig = getOrderStatusConfig(status);
  return {
    ...baseConfig,
    label: ADMIN_ORDER_STATUS_LABELS[status] || baseConfig.label,
  };
};

const AdminOrders = () => {
  // États
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Séparer les filtres serveur des filtres locaux
  const [serverFilters, setServerFilters] = useState({
    status: "",
    orderStatus: "",
    page: 1,
    limit: 20,
  });

  const [localSearch, setLocalSearch] = useState("");

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalOrders: 0,
  });
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState({
    orderId: "",
    currentStatus: "",
    newStatus: "",
    notes: "",
    tracking: {
      carrier: "",
      trackingNumber: "",
      trackingUrl: "",
    },
  });
  const [isUpdating, setIsUpdating] = useState(false);

  // Charger les commandes uniquement quand les filtres serveur changent
  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);

      const params = {
        page: serverFilters.page,
        limit: serverFilters.limit,
        sortBy: "createdAt",
        sortOrder: "desc",
      };

      if (serverFilters.orderStatus)
        params.orderStatus = serverFilters.orderStatus;
      if (serverFilters.status) params.status = serverFilters.status;

      const response = await getAllOrders(params);

      console.log("Réponse API commandes:", response.data); // Debug

      // Accéder correctement aux données
      if (response.data && response.data.orders) {
        // Vérifier que chaque commande a bien un orderStatus
        const ordersWithStatus = response.data.orders.map((order) => {
          if (!order.orderStatus) {
            console.warn(
              `Commande ${order.orderId} sans orderStatus, défaut: en_attente_paiement`
            );
          }
          return {
            ...order,
            orderStatus: order.orderStatus || ORDER_STATUS.EN_ATTENTE_PAIEMENT,
          };
        });

        setOrders(ordersWithStatus);
        setPagination(
          response.data.pagination || {
            currentPage: 1,
            totalPages: 1,
            totalOrders: ordersWithStatus.length,
          }
        );
      } else {
        // Fallback si la structure est différente
        const ordersArray = Array.isArray(response.data) ? response.data : [];
        setOrders(ordersArray);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des commandes:", error);
      toast.error("Impossible de charger les commandes");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [serverFilters]);

  // Charger les statistiques
  const loadStats = async () => {
    try {
      const response = await getOrderStats();
      console.log("Stats reçues:", response.data); // Debug
      setStats(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement des statistiques:", error);
    }
  };

  // Charger les données uniquement quand les filtres serveur changent
  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    loadStats();
  }, []);

  // Filtrer les commandes localement (recherche)
  const filteredOrders = useMemo(() => {
    if (!localSearch) return orders;

    const searchLower = localSearch.toLowerCase();
    return orders.filter((order) => {
      return (
        order.orderId?.toLowerCase().includes(searchLower) ||
        order.billingInfo?.email?.toLowerCase().includes(searchLower) ||
        order.billingInfo?.firstName?.toLowerCase().includes(searchLower) ||
        order.billingInfo?.lastName?.toLowerCase().includes(searchLower) ||
        order.paymentId?.toLowerCase().includes(searchLower) ||
        order.tracking?.trackingNumber?.toLowerCase().includes(searchLower)
      );
    });
  }, [orders, localSearch]);

  // Gérer le changement de page
  const handlePageChange = (newPage) => {
    setServerFilters({ ...serverFilters, page: newPage });
  };

  // Gérer le changement de filtres serveur
  const handleServerFilterChange = (key, value) => {
    setServerFilters({ ...serverFilters, [key]: value, page: 1 });
  };

  // Calculer les transitions possibles
  const getPossibleTransitions = useCallback((currentStatus) => {
    const transitions = {
      [ORDER_STATUS.EN_ATTENTE_PAIEMENT]: [
        ORDER_STATUS.PAYEE,
        ORDER_STATUS.ANNULEE,
      ],
      [ORDER_STATUS.PAYEE]: [ORDER_STATUS.EN_PREPARATION, ORDER_STATUS.ANNULEE],
      [ORDER_STATUS.EN_PREPARATION]: [
        ORDER_STATUS.EXPEDIEE,
        ORDER_STATUS.ANNULEE,
      ],
      [ORDER_STATUS.EXPEDIEE]: [ORDER_STATUS.LIVREE, ORDER_STATUS.RETOURNEE],
      [ORDER_STATUS.LIVREE]: [ORDER_STATUS.RETOURNEE],
      [ORDER_STATUS.ANNULEE]: [],
      [ORDER_STATUS.RETOURNEE]: [],
    };
    return transitions[currentStatus] || [];
  }, []);

  // Ouvrir le modal de changement de statut
  const openStatusModal = (order) => {
    console.log("Ouverture modal pour commande:", order); // Debug

    const possibleTransitions = getPossibleTransitions(order.orderStatus);

    setSelectedOrder(order);
    setStatusUpdate({
      orderId: order.orderId,
      currentStatus: order.orderStatus,
      newStatus: "", // Laisser vide pour forcer la sélection
      notes: "",
      tracking: {
        carrier: order.tracking?.carrier || "",
        trackingNumber: order.tracking?.trackingNumber || "",
        trackingUrl: order.tracking?.trackingUrl || "",
      },
    });
    setShowStatusModal(true);
  };

  // Fermer le modal
  const closeModal = () => {
    setShowStatusModal(false);
    setSelectedOrder(null);
    setStatusUpdate({
      orderId: "",
      currentStatus: "",
      newStatus: "",
      notes: "",
      tracking: {
        carrier: "",
        trackingNumber: "",
        trackingUrl: "",
      },
    });
  };

  // Mettre à jour le statut
  const handleStatusUpdate = async () => {
    // Validation
    if (!statusUpdate.newStatus) {
      toast.error("Veuillez sélectionner un nouveau statut");
      return;
    }

    if (statusUpdate.newStatus === statusUpdate.currentStatus) {
      toast.warning("Le nouveau statut doit être différent du statut actuel");
      return;
    }

    // Validation spécifique pour l'expédition
    if (statusUpdate.newStatus === ORDER_STATUS.EXPEDIEE) {
      if (
        !statusUpdate.tracking.carrier ||
        !statusUpdate.tracking.trackingNumber
      ) {
        toast.error(
          "Les informations de transporteur et numéro de suivi sont obligatoires pour marquer comme expédié"
        );
        return;
      }
    }

    try {
      setIsUpdating(true);

      const updateData = {
        orderStatus: statusUpdate.newStatus,
        notes:
          statusUpdate.notes ||
          `Statut mis à jour de ${
            ADMIN_ORDER_STATUS_LABELS[statusUpdate.currentStatus]
          } vers ${ADMIN_ORDER_STATUS_LABELS[statusUpdate.newStatus]}`,
      };

      // Ajouter les infos de tracking si on passe en "expédiée"
      if (statusUpdate.newStatus === ORDER_STATUS.EXPEDIEE) {
        updateData.tracking = {
          carrier: statusUpdate.tracking.carrier,
          trackingNumber: statusUpdate.tracking.trackingNumber,
          trackingUrl: statusUpdate.tracking.trackingUrl || "",
        };
      }

      console.log("Envoi mise à jour:", updateData); // Debug

      const response = await updateOrderStatus(
        statusUpdate.orderId,
        updateData
      );
      console.log("Réponse mise à jour:", response); // Debug

      toast.success("Statut mis à jour avec succès");
      closeModal();

      // Recharger les commandes et les stats
      await Promise.all([loadOrders(), loadStats()]);
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      toast.error(
        error.response?.data?.message ||
          "Erreur lors de la mise à jour du statut"
      );
    } finally {
      setIsUpdating(false);
    }
  };

  // Rendu du badge de statut avec les nouveaux labels
  const renderStatusBadge = (status) => {
    const config = getAdminOrderStatusConfig(status);
    return (
      <span
        className="badge d-inline-flex align-items-center"
        style={{
          backgroundColor: config.bgColor,
          color: config.textColor,
          fontSize: "0.875rem",
          padding: "0.375rem 0.75rem",
        }}
      >
        <i className={`bi ${config.icon} me-1`}></i>
        {config.label}
      </span>
    );
  };

  // Badge de statut de paiement
  const renderPaymentStatusBadge = (status) => {
    const configs = {
      paid: { label: "Payé", bgClass: "success" },
      pending: { label: "En attente", bgClass: "warning" },
      open: { label: "Ouvert", bgClass: "info" },
      failed: { label: "Échoué", bgClass: "danger" },
      canceled: { label: "Annulé", bgClass: "secondary" },
      expired: { label: "Expiré", bgClass: "dark" },
    };

    const config = configs[status] || { label: status, bgClass: "secondary" };

    return <span className={`badge bg-${config.bgClass}`}>{config.label}</span>;
  };

  // Rendu des statistiques
  const renderStats = () => {
    if (!stats) return null;

    // Compter les commandes par statut depuis les données actuelles
    const statusCounts = orders.reduce((acc, order) => {
      acc[order.orderStatus] = (acc[order.orderStatus] || 0) + 1;
      return acc;
    }, {});

    return (
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card">
            <div className="card-body">
              <h6 className="card-subtitle mb-2 text-muted">
                Total des commandes
              </h6>
              <h3 className="card-title mb-0">
                {stats.overview?.totalOrders || orders.length}
              </h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card">
            <div className="card-body">
              <h6 className="card-subtitle mb-2 text-muted">Revenu total</h6>
              <h3 className="card-title mb-0">
                {stats.overview?.totalRevenue?.toFixed(2) || "0.00"} €
              </h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card">
            <div className="card-body">
              <h6 className="card-subtitle mb-2 text-muted">Panier moyen</h6>
              <h3 className="card-title mb-0">
                {stats.overview?.averageOrderValue?.toFixed(2) || "0.00"} €
              </h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-primary text-white">
            <div className="card-body">
              <h6 className="card-subtitle mb-2 text-white-50">À traiter</h6>
              <h3 className="card-title mb-0">
                {statusCounts[ORDER_STATUS.PAYEE] || 0}
              </h3>
              <small className="text-white-50">Commandes reçues</small>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Calculer le montant total des articles
  const calculateItemsTotal = (items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce(
      (total, item) => total + item.price * (item.quantity || 1),
      0
    );
  };

  // Obtenir une description de l'action selon le nouveau statut
  const getActionDescription = (newStatus) => {
    const descriptions = {
      [ORDER_STATUS.PAYEE]: "Confirmer la réception du paiement",
      [ORDER_STATUS.EN_PREPARATION]: "Démarrer la préparation",
      [ORDER_STATUS.EXPEDIEE]: "Marquer comme expédiée",
      [ORDER_STATUS.LIVREE]: "Confirmer la livraison",
      [ORDER_STATUS.ANNULEE]: "Annuler la commande",
      [ORDER_STATUS.RETOURNEE]: "Marquer comme retournée",
    };
    return descriptions[newStatus] || "Mettre à jour le statut";
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-2">Chargement des commandes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h3 mb-0">Gestion des commandes</h1>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-primary"
            onClick={() => {
              loadOrders();
              loadStats();
              toast.success("Données actualisées");
            }}
          >
            <i className="bi bi-arrow-clockwise me-2"></i>
            Actualiser
          </button>
        </div>
      </div>

      {/* Statistiques */}
      {renderStats()}

      {/* Barre de recherche rapide */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="input-group">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Recherche rapide : N° commande, email, nom, n° de suivi..."
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
            />
            {localSearch && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setLocalSearch("")}
              >
                <i className="bi bi-x"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filtres serveur */}
      <div className="card mb-4">
        <div className="card-body">
          <h6 className="card-title mb-3">Filtres avancés</h6>
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">État de commande</label>
              <select
                className="form-select"
                value={serverFilters.orderStatus}
                onChange={(e) =>
                  handleServerFilterChange("orderStatus", e.target.value)
                }
              >
                <option value="">Tous les états</option>
                {Object.entries(ADMIN_ORDER_STATUS_LABELS).map(
                  ([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  )
                )}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Statut paiement</label>
              <select
                className="form-select"
                value={serverFilters.status}
                onChange={(e) =>
                  handleServerFilterChange("status", e.target.value)
                }
              >
                <option value="">Tous les statuts</option>
                <option value="paid">Payé</option>
                <option value="pending">En attente</option>
                <option value="open">Ouvert</option>
                <option value="failed">Échoué</option>
                <option value="canceled">Annulé</option>
                <option value="expired">Expiré</option>
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Commandes par page</label>
              <select
                className="form-select"
                value={serverFilters.limit}
                onChange={(e) =>
                  handleServerFilterChange("limit", parseInt(e.target.value))
                }
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          {/* Résumé des filtres actifs */}
          {(serverFilters.orderStatus ||
            serverFilters.status ||
            localSearch) && (
            <div className="mt-3 d-flex justify-content-between align-items-center">
              <small className="text-muted">
                {localSearch ? `Recherche: "${localSearch}" - ` : ""}
                {filteredOrders.length} commande(s) affichée(s) sur{" "}
                {orders.length}
              </small>
              <button
                className="btn btn-sm btn-link text-danger"
                onClick={() => {
                  setServerFilters({
                    status: "",
                    orderStatus: "",
                    page: 1,
                    limit: 20,
                  });
                  setLocalSearch("");
                }}
              >
                <i className="bi bi-x-circle me-1"></i>
                Réinitialiser
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Liste des commandes */}
      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th>N° Commande</th>
                <th>Date</th>
                <th>Client</th>
                <th>Montant</th>
                <th>État commande</th>
                <th>Paiement</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-4">
                    <i className="bi bi-inbox text-muted fs-1"></i>
                    <p className="mt-2 mb-0">
                      {localSearch
                        ? "Aucune commande ne correspond à votre recherche"
                        : "Aucune commande trouvée"}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => {
                  const possibleTransitions = getPossibleTransitions(
                    order.orderStatus
                  );

                  return (
                    <tr key={order._id}>
                      <td className="align-middle">
                        <span className="fw-bold">{order.orderId}</span>
                        {order.items?.some((item) => item.isSubscription) && (
                          <span className="badge bg-info ms-1">Abo</span>
                        )}
                      </td>
                      <td className="align-middle">
                        <small>
                          {formatDate(order.createdAt, {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </small>
                        <br />
                        <span
                          className="text-muted"
                          style={{ fontSize: "0.75rem" }}
                        >
                          {getTimeAgo(order.createdAt)}
                        </span>
                      </td>
                      <td className="align-middle">
                        <div>
                          <strong>
                            {order.billingInfo?.firstName}{" "}
                            {order.billingInfo?.lastName}
                          </strong>
                        </div>
                        <small className="text-muted">
                          {order.billingInfo?.email}
                        </small>
                      </td>
                      <td className="align-middle">
                        <span className="fw-bold">
                          {order.amount} {order.currency}
                        </span>
                      </td>
                      <td className="align-middle">
                        {renderStatusBadge(order.orderStatus)}
                        {order.tracking?.trackingNumber && (
                          <div className="mt-1">
                            <small className="text-muted">
                              <i className="bi bi-truck me-1"></i>
                              {order.tracking.carrier}
                            </small>
                          </div>
                        )}
                      </td>
                      <td className="align-middle">
                        {renderPaymentStatusBadge(order.status)}
                      </td>
                      <td className="align-middle">
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => openStatusModal(order)}
                            title={
                              possibleTransitions.length === 0
                                ? "Aucune transition possible"
                                : "Modifier le statut"
                            }
                          >
                            <i className="bi bi-pencil-square"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-info"
                            title="Voir les détails"
                            onClick={() => {
                              // TODO: Implémenter la vue détaillée
                              console.log("Détails de la commande:", order);
                              toast.info("Vue détaillée bientôt disponible");
                            }}
                          >
                            <i className="bi bi-eye"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="card-footer">
            <nav>
              <ul className="pagination justify-content-center mb-0">
                <li
                  className={`page-item ${
                    !pagination.hasPrevPage ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrevPage}
                  >
                    <i className="bi bi-chevron-left"></i> Précédent
                  </button>
                </li>

                {/* Affichage intelligent de la pagination */}
                {pagination.currentPage > 3 && (
                  <>
                    <li className="page-item">
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(1)}
                      >
                        1
                      </button>
                    </li>
                    {pagination.currentPage > 4 && (
                      <li className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    )}
                  </>
                )}

                {[...Array(Math.min(pagination.totalPages, 5))].map((_, i) => {
                  const pageNum = pagination.currentPage - 2 + i;
                  if (pageNum > 0 && pageNum <= pagination.totalPages) {
                    return (
                      <li
                        key={pageNum}
                        className={`page-item ${
                          pagination.currentPage === pageNum ? "active" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </button>
                      </li>
                    );
                  }
                  return null;
                })}

                {pagination.currentPage < pagination.totalPages - 2 && (
                  <>
                    {pagination.currentPage < pagination.totalPages - 3 && (
                      <li className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    )}
                    <li className="page-item">
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.totalPages)}
                      >
                        {pagination.totalPages}
                      </button>
                    </li>
                  </>
                )}

                <li
                  className={`page-item ${
                    !pagination.hasNextPage ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNextPage}
                  >
                    Suivant <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      {/* Modal de mise à jour du statut */}
      {showStatusModal && selectedOrder && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-pencil-square me-2"></i>
                  Mettre à jour la commande {selectedOrder.orderId}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                  disabled={isUpdating}
                ></button>
              </div>
              <div className="modal-body">
                {/* Informations de la commande */}
                <div className="alert alert-info">
                  <div className="row">
                    <div className="col-md-6">
                      <h6 className="mb-2">Informations client</h6>
                      <small className="d-block">
                        <strong>Nom:</strong>{" "}
                        {selectedOrder.billingInfo?.firstName}{" "}
                        {selectedOrder.billingInfo?.lastName}
                      </small>
                      <small className="d-block">
                        <strong>Email:</strong>{" "}
                        {selectedOrder.billingInfo?.email}
                      </small>
                      <small className="d-block">
                        <strong>Téléphone:</strong>{" "}
                        {selectedOrder.billingInfo?.phone || "Non renseigné"}
                      </small>
                    </div>
                    <div className="col-md-6">
                      <h6 className="mb-2">Informations commande</h6>
                      <small className="d-block">
                        <strong>Montant:</strong> {selectedOrder.amount}{" "}
                        {selectedOrder.currency}
                      </small>
                      <small className="d-block">
                        <strong>Date:</strong>{" "}
                        {formatDate(selectedOrder.createdAt)}
                      </small>
                      <small className="d-block">
                        <strong>Statut actuel:</strong>{" "}
                        {ADMIN_ORDER_STATUS_LABELS[selectedOrder.orderStatus]}
                      </small>
                      <small className="d-block">
                        <strong>Paiement:</strong>{" "}
                        {renderPaymentStatusBadge(selectedOrder.status)}
                      </small>
                    </div>
                  </div>

                  {/* Articles */}
                  {selectedOrder.items && selectedOrder.items.length > 0 && (
                    <div className="mt-3 pt-3 border-top">
                      <h6 className="mb-2">Articles commandés:</h6>
                      <div className="small">
                        {selectedOrder.items.map((item, index) => (
                          <div
                            key={index}
                            className="d-flex justify-content-between"
                          >
                            <span>
                              {item.quantity || 1} x {item.name}
                              {item.isSubscription && (
                                <span className="badge bg-info ms-1">Abo</span>
                              )}
                            </span>
                            <span>
                              {(item.price * (item.quantity || 1)).toFixed(2)} €
                            </span>
                          </div>
                        ))}
                        <div className="d-flex justify-content-between mt-2 pt-2 border-top">
                          <strong>Total articles:</strong>
                          <strong>
                            {calculateItemsTotal(selectedOrder.items).toFixed(
                              2
                            )}{" "}
                            €
                          </strong>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* État actuel */}
                <div className="mb-3 p-3 bg-light rounded">
                  <div className="d-flex align-items-center">
                    <span className="me-2">État actuel:</span>
                    {renderStatusBadge(selectedOrder.orderStatus)}
                  </div>
                </div>

                {/* Changement de statut */}
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    <i className="bi bi-arrow-right-circle me-1"></i>
                    Nouveau statut
                  </label>
                  {getPossibleTransitions(selectedOrder.orderStatus).length ===
                  0 ? (
                    <div className="alert alert-warning mb-0">
                      <i className="bi bi-info-circle me-2"></i>
                      Aucune transition possible depuis l'état "
                      {ADMIN_ORDER_STATUS_LABELS[selectedOrder.orderStatus]}".
                      Cet état est final.
                    </div>
                  ) : (
                    <>
                      <select
                        className="form-select form-select-lg"
                        value={statusUpdate.newStatus}
                        onChange={(e) => {
                          console.log("Changement de statut:", e.target.value); // Debug
                          setStatusUpdate({
                            ...statusUpdate,
                            newStatus: e.target.value,
                          });
                        }}
                        disabled={isUpdating}
                      >
                        <option value="">
                          -- Sélectionner un nouveau statut --
                        </option>
                        {getPossibleTransitions(selectedOrder.orderStatus).map(
                          (status) => {
                            const config = getAdminOrderStatusConfig(status);
                            return (
                              <option key={status} value={status}>
                                {config.label}
                              </option>
                            );
                          }
                        )}
                      </select>
                      <small className="form-text text-muted">
                        Transitions possibles depuis "
                        {ADMIN_ORDER_STATUS_LABELS[selectedOrder.orderStatus]}"
                      </small>
                    </>
                  )}
                </div>

                {/* Aperçu de la transition */}
                {statusUpdate.newStatus && (
                  <div className="alert alert-primary d-flex align-items-center mb-3">
                    <i className="bi bi-arrow-right-circle-fill me-2"></i>
                    <div>
                      <strong>
                        Action: {getActionDescription(statusUpdate.newStatus)}
                      </strong>
                      <div className="mt-1">
                        {renderStatusBadge(selectedOrder.orderStatus)}
                        <i className="bi bi-arrow-right mx-2"></i>
                        {renderStatusBadge(statusUpdate.newStatus)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Informations de tracking (si expédié) */}
                {statusUpdate.newStatus === ORDER_STATUS.EXPEDIEE && (
                  <div className="border rounded p-3 mb-3 bg-light">
                    <h6>
                      <i className="bi bi-truck me-2"></i>Informations de
                      livraison (obligatoire)
                    </h6>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label">
                          Transporteur <span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-select"
                          value={statusUpdate.tracking.carrier}
                          onChange={(e) =>
                            setStatusUpdate({
                              ...statusUpdate,
                              tracking: {
                                ...statusUpdate.tracking,
                                carrier: e.target.value,
                              },
                            })
                          }
                          disabled={isUpdating}
                          required
                        >
                          <option value="">-- Sélectionner --</option>
                          <option value="Bpost">Bpost</option>
                          <option value="DHL">DHL</option>
                          <option value="PostNL">PostNL</option>
                          <option value="DPD">DPD</option>
                          <option value="UPS">UPS</option>
                          <option value="FedEx">FedEx</option>
                          <option value="Colissimo">Colissimo</option>
                          <option value="Mondial Relay">Mondial Relay</option>
                          <option value="Autre">Autre</option>
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">
                          N° de suivi <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Ex: 1234567890"
                          value={statusUpdate.tracking.trackingNumber}
                          onChange={(e) =>
                            setStatusUpdate({
                              ...statusUpdate,
                              tracking: {
                                ...statusUpdate.tracking,
                                trackingNumber: e.target.value,
                              },
                            })
                          }
                          disabled={isUpdating}
                          required
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">
                          URL de suivi (optionnel)
                        </label>
                        <input
                          type="url"
                          className="form-control"
                          placeholder="https://..."
                          value={statusUpdate.tracking.trackingUrl}
                          onChange={(e) =>
                            setStatusUpdate({
                              ...statusUpdate,
                              tracking: {
                                ...statusUpdate.tracking,
                                trackingUrl: e.target.value,
                              },
                            })
                          }
                          disabled={isUpdating}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Informations complémentaires selon le statut */}
                {statusUpdate.newStatus === ORDER_STATUS.ANNULEE && (
                  <div className="alert alert-danger">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    <strong>Attention:</strong> L'annulation de la commande est
                    définitive. Assurez-vous d'avoir contacté le client avant de
                    procéder.
                  </div>
                )}

                {statusUpdate.newStatus === ORDER_STATUS.LIVREE && (
                  <div className="alert alert-success">
                    <i className="bi bi-check-circle me-2"></i>
                    Cette action confirmera que la commande a été livrée avec
                    succès au client.
                  </div>
                )}

                {/* Notes */}
                <div className="mb-3">
                  <label className="form-label">
                    Notes internes (optionnel)
                  </label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Ajouter une note sur cette mise à jour..."
                    value={statusUpdate.notes}
                    onChange={(e) =>
                      setStatusUpdate({
                        ...statusUpdate,
                        notes: e.target.value,
                      })
                    }
                    disabled={isUpdating}
                  ></textarea>
                  <small className="form-text text-muted">
                    Ces notes sont visibles uniquement par l'équipe
                    administrative
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                  disabled={isUpdating}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleStatusUpdate}
                  disabled={
                    !statusUpdate.newStatus ||
                    isUpdating ||
                    getPossibleTransitions(selectedOrder.orderStatus).length ===
                      0
                  }
                >
                  {isUpdating ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Mise à jour...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      {statusUpdate.newStatus
                        ? getActionDescription(statusUpdate.newStatus)
                        : "Mettre à jour"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
